import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/** The product's main colour: buttons, links, the selected menu item. */
export const PRIMARY_COLOR = '#7c3deb';

/** The tint of the greys (page background, borders), as in the reference design. */
export const SURFACE_COLOR = '#f5f5fa';

/**
 * A surface scale tinted with a colour. PrimeNG's palette() puts the given colour at shade 500,
 * which for a light "surface colour" makes the muted text near white; a surface is a family of
 * neutrals, so this keeps the colour's hue and a little of its saturation on the standard
 * lightness ladder, from almost white to almost black.
 */
export function surfacePalette(hex: string): Record<string, string> {
  const [h, s] = hexToHsl(hex);
  const sat = Math.min(s, 0.18);
  const ladder: [string, number][] = [
    ['0', 1],
    ['50', 0.98],
    ['100', 0.955],
    ['200', 0.905],
    ['300', 0.83],
    ['400', 0.7],
    ['500', 0.56],
    ['600', 0.44],
    ['700', 0.35],
    ['800', 0.25],
    ['900', 0.15],
    ['950', 0.08],
  ];
  const out: Record<string, string> = {};
  for (const [shade, l] of ladder) {
    out[shade] = hslToHex(h, shade === '0' ? 0 : sat, l);
  }
  return out;
}

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) {
    return [0, 0, l];
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) {
    h = (g - b) / d + (g < b ? 6 : 0);
  } else if (max === g) {
    h = (b - r) / d + 2;
  } else {
    h = (r - g) / d + 4;
  }
  return [h / 6, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const f = (n: number): string => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

const surface = surfacePalette(SURFACE_COLOR);

/**
 * The primary colour chosen in the Settings panel, if any (AppStateService keeps it in localStorage). PrimeNG
 * starts with it, so a saved colour is there from the first paint instead of being applied after the page loads.
 */
function savedPrimaryColor(): string {
  try {
    const saved = JSON.parse(localStorage.getItem('Ynex-ng') ?? '{}') as { themePrimary?: string };
    return /^#[0-9a-f]{6}$/i.test(saved.themePrimary ?? '') ? (saved.themePrimary as string) : PRIMARY_COLOR;
  } catch {
    return PRIMARY_COLOR;
  }
}

/** PrimeNG's Aura preset with this product's colours. */
export const MasterPreset = definePreset(Aura, {
  semantic: {
    primary: palette(savedPrimaryColor()),
    colorScheme: {
      light: { surface },
      dark: { surface },
    },
  },
});
